import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from './api-client.service';

export interface SellerFanState {
  following: boolean;
  followerCount: number;
  loaded: boolean;
}

@Injectable({ providedIn: 'root' })
export class SellerFanService {
  private readonly api = inject(ApiClientService);
  private readonly states = signal<Record<number, SellerFanState>>({});
  private readonly pending = new Set<number>();
  private readonly waiters = new Map<number, Array<(state: SellerFanState) => void>>();
  private flushScheduled = false;

  state(sellerID: number, initialCount = 0): SellerFanState {
    const id = Number(sellerID || 0);
    return this.states()[id] ?? { following: false, followerCount: Math.max(0, Number(initialCount || 0)), loaded: false };
  }

  ensure(sellerID: number, initialCount = 0): Promise<SellerFanState> {
    const id = Number(sellerID || 0);
    if (id <= 0) return Promise.resolve({ following: false, followerCount: 0, loaded: true });
    const current = this.state(id, initialCount);
    if (current.loaded) return Promise.resolve(current);
    this.setState(id, { ...current, followerCount: Math.max(current.followerCount, Number(initialCount || 0)) });
    this.pending.add(id);
    const promise = new Promise<SellerFanState>((resolve) => {
      const callbacks = this.waiters.get(id) ?? [];
      callbacks.push(resolve);
      this.waiters.set(id, callbacks);
    });
    if (!this.flushScheduled) {
      this.flushScheduled = true;
      queueMicrotask(() => void this.flush());
    }
    return promise;
  }

  async toggle(sellerID: number, follow: boolean, initialCount = 0): Promise<SellerFanState> {
    const id = Number(sellerID || 0);
    const before = this.state(id, initialCount);
    const response: any = await firstValueFrom(this.api.post('feedFollow', { sellerID: id, follow }));
    const data = response?.data?.data ?? response?.data ?? response ?? {};
    const state: SellerFanState = {
      following: data.isFollowing !== undefined ? !!data.isFollowing : follow,
      followerCount: Number.isFinite(Number(data.followerCount))
        ? Math.max(0, Number(data.followerCount))
        : Math.max(0, before.followerCount + (follow ? 1 : -1)),
      loaded: true
    };
    this.setState(id, state);
    return state;
  }

  private async flush(): Promise<void> {
    this.flushScheduled = false;
    const ids = [...this.pending].slice(0, 100);
    ids.forEach((id) => this.pending.delete(id));
    if (!ids.length) return;
    try {
      const response: any = await firstValueFrom(this.api.post('sellerFollowState', { sellerIDs: ids }));
      const data = response?.data?.data ?? response?.data ?? response ?? {};
      const followed = new Set((Array.isArray(data.followingSellerIDs) ? data.followingSellerIDs : []).map(Number));
      ids.forEach((id) => this.finish(id, { ...this.state(id), following: followed.has(id), loaded: true }));
    } catch {
      ids.forEach((id) => this.finish(id, { ...this.state(id), loaded: true }));
    }
    if (this.pending.size && !this.flushScheduled) {
      this.flushScheduled = true;
      queueMicrotask(() => void this.flush());
    }
  }

  private finish(id: number, state: SellerFanState): void {
    this.setState(id, state);
    const callbacks = this.waiters.get(id) ?? [];
    this.waiters.delete(id);
    callbacks.forEach((resolve) => resolve(state));
  }

  private setState(id: number, state: SellerFanState): void {
    this.states.update((values) => ({ ...values, [id]: state }));
  }
}
